<template>
    <div>
        <div class="box view-nav-bar">
            <div class="d-flex align-items-center w-100">
                <div class="flex-grow-1">
                    <strong>Per movie</strong>
                    <span class="text-muted ml-2">— top {{ bucket.top || 10 }} movies per cinema year, all sessions vs. Brightfish preshow</span>
                </div>
                <form class="form-inline" @submit.prevent="goToMovie">
                    <label class="text-muted mr-2" for="movix-id">Movix id</label>
                    <input id="movix-id" v-model.trim="movixId" type="text" pattern="\d+" class="form-control form-control-sm shx-th-100" placeholder="12345">
                    <button type="submit" class="btn btn-sm btn-outline-secondary ml-2" :disabled="!/^\d+$/.test(movixId)">Open</button>
                </form>
            </div>
        </div>

        <div v-if="loading" class="box">
            <div class="box-content text-center text-muted py-4">Loading…</div>
        </div>

        <div v-else-if="!rows.length" class="box">
            <div class="box-content text-center text-muted py-4">No admissions data found.</div>
        </div>

        <template v-else>
            <div v-for="year in rows" :key="year.year" class="box">
                <div class="box-content">
                    <div class="d-flex align-items-center">
                        <heading :lower="formatInt(year.admissions) + ' admissions, ' + formatInt(year.admissions_preshow) + ' preshow (' + (year.pct_preshow !== null ? year.pct_preshow : 0) + '%), ' + year.movies + ' movies over ' + year.weeks + ' weeks in ' + year.cinemas + ' cinemas'"
                                 border class="flex-grow-1">
                            <template v-slot:upper>
                                <router-link :to="'/admissions/movie/year/' + year.year">Cinema year {{ year.year }}</router-link>
                            </template>
                        </heading>
                        <router-link :to="'/admissions/movie/year/' + year.year" class="btn btn-sm btn-outline-secondary ml-3">
                            Top 100
                        </router-link>
                    </div>
                    <table class="shx-table w-100 mt-2">
                        <thead>
                            <tr>
                                <th class="shx-th-50">#</th>
                                <th class="shx-th-80">Movix</th>
                                <th>Title</th>
                                <th class="shx-th-120">Tag</th>
                                <th class="text-right shx-th-100"># Weeks</th>
                                <th class="text-right">Admissions</th>
                                <th class="text-right shx-th-100">% of year</th>
                                <th class="text-right shx-th-120">Preshow (BF)</th>
                                <th class="text-right shx-th-120">% of preshow</th>
                                <th class="text-right shx-th-100">Real %</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(movie, index) in year.top" :key="movie.movix_id">
                                <td class="text-muted">{{ index + 1 }}</td>
                                <td>
                                    <router-link :to="'/admissions/movie/' + movie.movix_id">{{ movie.movix_id }}</router-link>
                                </td>
                                <td>
                                    <router-link :to="'/admissions/movie/' + movie.movix_id">{{ movie.title || '—' }}</router-link>
                                </td>
                                <td>
                                    <router-link :to="{ path: '/admissions/movie/year/' + year.year, query: { tag: movie.movie_tag || 'none' } }"
                                                 :class="{ 'text-muted': !movie.movie_tag }">{{ movie.movie_tag || 'no tag' }}</router-link>
                                </td>
                                <td class="text-right">{{ movie.weeks }}</td>
                                <td class="text-right">{{ formatInt(movie.admissions) }}</td>
                                <td class="text-right">{{ movie.pct !== null ? movie.pct + '%' : '—' }}</td>
                                <td class="text-right">{{ formatInt(movie.admissions_preshow) }}</td>
                                <td class="text-right">{{ movie.pct_preshow !== null ? movie.pct_preshow + '%' : '—' }}</td>
                                <td class="text-right">
                                    <span>{{ movie.real_weighted != null ? movie.real_weighted + '%' : '—' }}</span>
                                    <span v-if="movie.real_weighted != null && movie.real_weighted <= 90"
                                          class="badge badge-warning ml-1">⚠</span>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colspan="5"><strong>Top {{ year.top.length }}</strong></td>
                                <td class="text-right"><strong>{{ formatInt(sum(year.top, 'admissions')) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(sum(year.top, 'admissions'), year.admissions) }}</strong></td>
                                <td class="text-right"><strong>{{ formatInt(sum(year.top, 'admissions_preshow')) }}</strong></td>
                                <td class="text-right"><strong>{{ pct(sum(year.top, 'admissions_preshow'), year.admissions_preshow) }}</strong></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
            <div class="box">
                <div class="box-content">
                    <p class="text-muted small mb-0">
                        A cinema year runs from week C01 to the last cinema week (Wednesday to Tuesday).
                        <strong>Admissions</strong> = every <code>admissions_week</code> row of the movie, whatever the sales house;
                        <strong>Preshow</strong> = rows with <code>saleshouse = BF</code>. The year totals include titles
                        without a Movix id, so the shares are of everything reported that year.
                        <strong>Real %</strong> is the admissions-weighted average of <code>real</code>: 97.5 = fully
                        compiled from Cinedata, lower = partly estimated.
                        Same data as JSON: <code>GET /api/v1/admissions/movie</code>.
                    </p>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import axios from 'axios';
import Heading from '../../partials/Heading';

export default {
    name: 'AdmissionsMovieYears',
    components: {
        Heading,
    },
    data() {
        return {
            loading: true,
            bucket: {},
            rows: [],
            movixId: '',
        };
    },
    created() {
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            axios
                .get('/admissions/movie')
                .then(response => {
                    this.bucket = response.data.data.bucket || {};
                    this.rows = response.data.data.rows || [];
                })
                .catch(error => {
                    this.$toast.error(error.response?.data?.error?.message || error.message);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        goToMovie() {
            if (/^\d+$/.test(this.movixId)) {
                this.$router.push('/admissions/movie/' + this.movixId);
            }
        },
        sum(rows, field) {
            return (rows || []).reduce((total, r) => total + (r[field] || 0), 0);
        },
        pct(part, total) {
            return total ? (Math.round(part / total * 1000) / 10) + '%' : '—';
        },
        formatInt(v) {
            return Math.round(v || 0).toLocaleString();
        },
    },
};
</script>
